import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { registry, type AssetRowIn, type PartRowIn, type RegCompany } from "@/api/client";
import { CompanyFilter } from "@/components/CompanyFilter";
import { Field } from "@/components/Field";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { Select, SearchInput } from "@/components/inputs";
import { CAP, useSession } from "@/auth/session";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { KindSelect, useManufacturers, useModelPool, useTemplates } from "@/modules/asset_catalog/common";

import { ActionRow } from "@/components/ActionRow";
/** The LIFECYCLE states (REGISTRY.md §10). `installed` is deliberately absent:
 *  it was never a life stage, it is a structural fact that co-exists with any
 *  state (a part fitted to a deployed asset is `deployed` AND installed), and
 *  the API says so through `installed_in`. */
export const PART_CHIP: StatusMap = {
  inventory: { label: "INVENTORY", color: "var(--teal)" },
  deployed: { label: "DEPLOYED", color: "var(--ok)" },
  repair: { label: "REPAIR", color: "var(--warn)" },
  scrapped: { label: "SCRAPPED", color: "var(--t-mute)" },
};
export const PartChip = ({ st }: { st: string }) => <StatusChip st={st} map={PART_CHIP} />;

// The __inventory__ sentinel is GONE (REGISTRY.md §10). "In stock" is a STATE
// and it is always somebody's; you never un-own a thing, you transfer it back.
// Kept only as a doc comment so nobody reintroduces it by copying an old call.
export const RQ = ["registry"] as const;

export function useCompanies() {
  return useQuery({ queryKey: [...RQ, "companies"], queryFn: registry.companies });
}

/** The list AND whether the server filtered it, from one call.
 *
 *  `useCompanies().data?.companies ?? []` was the idiom everywhere, and it
 *  throws `scoped` away — which is how six call sites would otherwise have had
 *  to remember to fetch a second thing. Handing both back together means a
 *  caller cannot take the list and forget the flag. Defaults to NOT scoped
 *  while loading: an unnecessary dropdown for a moment is cosmetic, whereas
 *  wrongly collapsing hides controls that only an operator can use. */
export function useCompanyScope() {
  const q = useCompanies();
  return { companies: q.data?.companies ?? [], scoped: q.data?.scoped ?? false };
}

/** Company filter/selector with the stock sentinel. */
/** Registry's company picker — now a thin wrapper over the shared
 *  CompanyFilter, so a single-company caller sees their company as text rather
 *  than an "All companies" dropdown listing one entry. Kept as a named export
 *  because a dozen call sites use it and they read better with the local name.
 */
export function CompanySelect({ value, onChange, companies, scoped, allLabel = "All companies", style }: {
  value: string; onChange: (v: string) => void; companies: RegCompany[];
  /** From the endpoint that returned `companies` — see CompanyFilter. Required
   *  here too rather than defaulted, so a call site cannot quietly pick the
   *  wrong meaning of a one-row list. */
  scoped: boolean;
  allLabel?: string | null;
  style?: React.CSSProperties;
}) {
  return (
    <CompanyFilter companies={companies} scoped={scoped} value={value} onChange={onChange}
                   allLabel={allLabel} label={null} style={style} />
  );
}

/** Kind → Manufacturer (Asset Catalog, suppliers of that kind) → active Model.
 *  The manufacturer is DERIVED from the model on the server; here it only
 *  narrows the model list. `kinds` restricts the kind picker. */
export function ModelPicker({ kinds, kind, onKind, modelId, onModel, lockKind = false }: {
  kinds: string[]; kind: string; onKind: (k: string) => void; modelId: string; onModel: (id: string) => void;
  lockKind?: boolean;
}) {
  const tpl = useTemplates().data;
  const mfrs = useManufacturers().data?.manufacturers ?? [];
  const pool = useModelPool().data?.models ?? [];
  const [mfr, setMfr] = useState("");
  const mfrOpts = mfrs.filter((m) => m.status === "active" && kind && m.supplies.includes(kind));
  const models = pool.filter((m) => m.kind === kind && m.status === "active" && (!mfr || m.manufacturer_id === mfr));
  return (
    <>
      {!lockKind && (
        <Field label="Kind" req>
          <KindSelect kinds={tpl?.kinds ?? []} only={kinds} value={kind}
            onChange={(k) => { onKind(k); setMfr(""); onModel(""); }} style={{ width: "100%", minWidth: 0 }} />
        </Field>
      )}
      <Field label="Manufacturer" req>
        <Select className="fill" value={mfr} disabled={!kind} onChange={(e) => { setMfr(e.target.value); onModel(""); }}>
          <option value="">{!kind ? "choose kind first" : mfrOpts.length ? "choose" : "none supplies this kind (Asset Catalog → Manufacturers)"}</option>
          {mfrOpts.map((m) => <option key={m.manufacturer_id} value={m.manufacturer_id}>{m.abbr ? `${m.abbr} - ${m.name}` : m.name}</option>)}
        </Select>
      </Field>
      <Field label="Catalog model" req>
        <Select className="fill" value={modelId} disabled={!mfr} onChange={(e) => onModel(e.target.value)}>
          <option value="">{!mfr ? "choose manufacturer first" : models.length ? "choose" : "no active model (activate one in the Asset Catalog)"}</option>
          {models.map((m) => <option key={m.version_id} value={m.version_id}>{m.name} · v{m.version}</option>)}
        </Select>
      </Field>
    </>
  );
}

export const SUBCOMPONENT_KINDS = ["tcu", "bms_c", "vcu_c", "cluster_c", "cell", "bms", "motor", "mcu", "dcdc", "vcu", "cluster"];

/** Single-part enroll — the Asset Catalog form grammar (New subcomponent
 *  model): sectioned, uncluttered. Connectivity appears only for connected
 *  kinds; Bluetooth only when the chosen model has the BLE tick. Bulk lives
 *  on its own grid — never clubbed with this. */
export function EnrollPartForm({ owner, ownerField, onDone }: {
  /** The company that will own AND hold what is enrolled, chosen once on the
   *  Enroll page. Required: the server would otherwise fall back to the
   *  session's company, which for a super admin is a context, not an owner. */
  owner: string;
  /** The picker itself. The page owns the STATE (one answer for all four enrol
   *  doors); the form places it, as a titled group beside Identity and
   *  Provenance, because who owns the part is a fact about the part. */
  ownerField: React.ReactNode;
  onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  // ENROL — hiding is UX; require_cap on the route is the enforcement.
  const { can: allowed } = useSession();
  if (!allowed(CAP.ENROL)) {
    return <div className="cap">Enrolling creates platform identities - a super-admin operation.</div>;
  }

  const fb = useMutationFeedback();
  const tpl = useTemplates().data;
  const pool = useModelPool().data?.models ?? [];
  const [kind, setKind] = useState("");
  const [modelId, setModelId] = useState("");
  const [f, setF] = useState({ serial_no: "", connection_id: "", ble_mac: "", board_id: "", iccid: "", imsi: "",
    manufactured_at: "", w_years: "", w_months: "", lot: "" });
  //: THE IMEI ONLY, NO SECRET (0098). This held a broker password in React
  //: state, rendered it on screen and offered a Copy button that put it on
  //: the system clipboard. Dattatray: *"Even though it is bench passowrd its
  //: still a a password and we can not hand it out like this"*. The server no
  //: longer returns one; what the banner is actually for is confirming WHICH
  //: connection the part was enrolled against.
  const [cred, setCred] = useState<{ imei: string } | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((p) => ({ ...p, [k]: e.target.value }));
  // Warranty is entered as a PERIOD (years + months); the stored fact stays a
  // DATE: manufactured + period, month-arithmetic with end-of-month clamping
  // (Jan 31 + 1 month = Feb 28/29). No manufactured date -> nothing to compute.
  const warrantyEnd = ((): string | null => {
    const y = parseInt(f.w_years, 10) || 0, mo = parseInt(f.w_months, 10) || 0;
    if (!f.manufactured_at || (y === 0 && mo === 0)) return null;
    const d = new Date(f.manufactured_at + "T00:00:00Z");
    if (isNaN(d.getTime())) return null;
    const total = d.getUTCMonth() + y * 12 + mo;
    const yy = d.getUTCFullYear() + Math.floor(total / 12), mm = total % 12;
    const day = Math.min(d.getUTCDate(), new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate());
    return new Date(Date.UTC(yy, mm, day)).toISOString().slice(0, 10);
  })();
  // MAC entry: type hex, the colons place themselves (AA:BB:CC:DD:EE:FF).
  // A shrinking value is a deletion — store it as-is, or backspace would
  // fight the formatter over the just-removed colon.
  const setMac = (e: React.ChangeEvent<HTMLInputElement>) => setF((p) => {
    const v = e.target.value;
    if (v.length < p.ble_mac.length) return { ...p, ble_mac: v };
    const hex = v.replace(/[^0-9a-fA-F]/g, "").toUpperCase().slice(0, 12);
    return { ...p, ble_mac: (hex.match(/.{1,2}/g) ?? []).join(":") };
  });
  const conn = !!tpl?.kinds.find((k) => k.id === kind)?.conn;
  const hasBle = pool.find((m) => m.version_id === modelId)?.derived?.has_ble === true;

  const enroll = useMutation({
    mutationFn: () => {
      const row: PartRowIn = { kind, version_id: modelId, serial_no: f.serial_no.trim(),
        connection_id: conn ? f.connection_id.trim() : null,
        ble_mac: hasBle ? f.ble_mac.trim() : null,
        board_id: f.board_id.trim() || null,
        iccid: conn ? f.iccid.trim() || null : null, imsi: conn ? f.imsi.trim() || null : null,
        manufactured_at: f.manufactured_at || null, warranty_until: warrantyEnd, lot: f.lot.trim() || null };
      return registry.enrollParts([row], owner);
    },
    onSuccess: (r) => {
      fb.ok(); void qc.invalidateQueries({ queryKey: [...RQ] });
      const e = r.enrolled[0];
      onDone(`${e.serial_no} enrolled${e.connection_id ? ` · Connection ${e.connection_id}` : ""}${e.ble_mac ? ` · BLE ${e.ble_mac}` : ""}`);
      setCred(e.credential && e.connection_id ? { imei: e.connection_id } : null);
      setF({ serial_no: "", connection_id: "", ble_mac: "", board_id: "", iccid: "", imsi: "", manufactured_at: "", w_years: "", w_months: "", lot: "" });
    },
    onError: fb.fail,
  });
  const can = !!kind && !!modelId && !!f.serial_no.trim()
    && (!conn || f.connection_id.trim().length === 15) && (!hasBle || f.ble_mac.trim().length >= 12);
  const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 };

  return (
    <div>
      <div className="section-label">Owner</div>
      <div style={grid}>
        <Field label="Company" req>{ownerField}</Field>
      </div>

      <div className="section-label tight">Identity</div>
      <div style={grid}>
        <ModelPicker kinds={SUBCOMPONENT_KINDS} kind={kind} onKind={(k) => { setKind(k); setCred(null); }} modelId={modelId} onModel={setModelId} />
        <Field label="Serial no." req>
          <SearchInput className="fill" mono value={f.serial_no} onChange={set("serial_no")} placeholder="as printed on the part" />
        </Field>
        {kind && !conn && (
          <Field label="Board ID">
            <SearchInput className="fill" mono value={f.board_id} onChange={set("board_id")}
              placeholder="empty = same as serial" />
          </Field>
        )}
      </div>

      {conn && (<>
        <div className="section-label tight">Connectivity</div>
        <div style={grid}>
          <Field label="Connection ID (IMEI)" req>
            <SearchInput className="fill" mono value={f.connection_id} onChange={set("connection_id")} placeholder="15 digits" maxLength={15} />
          </Field>
          <Field label="Board ID">
            <SearchInput className="fill" mono value={f.board_id} onChange={set("board_id")} placeholder="BRD-000123" />
          </Field>
          <Field label="ICCID (if SIM fitted)">
            <SearchInput className="fill" mono value={f.iccid} onChange={set("iccid")} />
          </Field>
          <Field label="IMSI">
            <SearchInput className="fill" mono value={f.imsi} onChange={set("imsi")} />
          </Field>
        </div>
        <div style={{ fontSize: 12, color: "var(--t-mute)", marginTop: 8 }}>
          Enrolling creates the part's Connection and issues the broker credential (shown below to bake), or adopts an existing unallocated Connection.
        </div>
      </>)}

      {hasBle && (<>
        <div className="section-label tight">Bluetooth</div>
        <div style={grid}>
          <Field label="BLE MAC" req>
            <SearchInput className="fill" mono value={f.ble_mac} onChange={setMac} placeholder="AA:BB:CC:DD:EE:FF" maxLength={17} />
          </Field>
        </div>
        <div style={{ fontSize: 12, color: "var(--t-mute)", marginTop: 8 }}>
          This model has the BLE tick in the Asset Catalog - the unit's MAC is required.
        </div>
      </>)}

      <div className="section-label tight">Provenance</div>
      <div style={grid}>
        <Field label="Manufactured"><SearchInput className="fill" type="date" value={f.manufactured_at} onChange={set("manufactured_at")} /></Field>
        <Field label="Warranty period">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <SearchInput className="fill" type="number" mono min={0} placeholder="years" value={f.w_years} onChange={set("w_years")} />
            <SearchInput className="fill" type="number" mono min={0} max={11} placeholder="months" value={f.w_months} onChange={set("w_months")} />
          </div>
          <div style={{ fontSize: 11.5, color: warrantyEnd ? "var(--t-mute)" : "var(--t-dim)", marginTop: 4 }}>
            {warrantyEnd ? `ends ${warrantyEnd}`
              : (f.w_years || f.w_months) ? "set the Manufactured date to compute the end"
              : "years + months from the Manufactured date"}
          </div>
        </Field>
        <Field label="Lot / batch"><SearchInput className="fill" mono value={f.lot} onChange={set("lot")} /></Field>
      </div>

      <ActionRow top={18}>
        {fb.errorNode}
        {/* !owner too: the picker sits at the top of this panel, and an
            action that fires without one would let the server infer the owner
            from the session - the thing the picker exists to stop. */}
        <button className="btn teal" disabled={!can || !owner || enroll.isPending}
                onClick={() => enroll.mutate()}>Enroll part</button>
      </ActionRow>

      {cred && (
        <div style={{ marginTop: 14, padding: "12px 14px", border: "1px solid color-mix(in srgb, var(--ok) 40%, transparent)", borderRadius: 10, background: "color-mix(in srgb, var(--ok) 6%, transparent)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "var(--ok)", letterSpacing: ".5px", textTransform: "uppercase" }}>Enrolled - broker credential provisioned</div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, alignItems: "center", fontSize: 12.5 }}>
            <span className="muted">Connection ID</span>
            <span className="mono" style={{ color: "var(--t-bright)", fontWeight: 600, background: "var(--surface)", border: "1px solid var(--line2)", padding: "4px 9px", borderRadius: 6 }}>{cred.imei}</span>
            {/* NO PASSWORD, AND NO COPY BUTTON. Under mTLS the device
                authenticates with a certificate and there is nothing to bake;
                the v1 password was shared and already in the firmware image,
                so showing it told an operator nothing they could act on and
                put a secret on screen, in screenshots, and -- via Copy -- on
                the system clipboard. */}
            <button className="btn ghost" onClick={() => setCred(null)}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- strict CSV parsing (one toolkit, one template per door) ----------------
 * The template IS the contract: unknown or missing columns reject the file,
 * every row is checked with its row number, and nothing is submittable until
 * the sheet is clean. Values must not contain commas (the templates carry
 * none; no quoting support). Server-side validation stays authoritative. */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SERIAL_CSV_RE = /^[A-Za-z0-9][A-Za-z0-9._/-]{1,63}$/;
const MAC_CSV_RE = /^[0-9A-Fa-f]{2}([:-][0-9A-Fa-f]{2}){5}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const CONN_KINDS = new Set(["tcu", "bms_c", "vcu_c", "cluster_c"]);

function parseStrict(text: string, required: string[], optional: string[],
  rowCheck: (o: Record<string, string>, addErr: (msg: string) => void) => void): Record<string, string>[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) throw new Error("empty file");
  const head = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const known = new Set([...required, ...optional]);
  const issues: string[] = [];
  for (const r of required) if (!head.includes(r)) issues.push(`missing column '${r}' - download the current template`);
  for (const h of head) if (h && !known.has(h)) issues.push(`unknown column '${h}' - download the current template`);
  if (issues.length) throw new Error(issues.join("\n"));
  if (lines.length === 1) throw new Error("no data rows under the header");
  const rows: Record<string, string>[] = [];
  lines.slice(1).forEach((l, n) => {
    const cells = l.split(",").map((c) => c.trim());
    if (cells.length > head.length) { issues.push(`row ${n + 1}: ${cells.length} values for ${head.length} columns (commas inside values are not supported)`); return; }
    const o: Record<string, string> = {};
    head.forEach((h, i2) => { o[h] = cells[i2] ?? ""; });
    if (!Object.values(o).some(Boolean)) return;          // blank line
    for (const r of required) if (!o[r]) issues.push(`row ${n + 1}: '${r}' is empty`);
    rowCheck(o, (msg) => issues.push(`row ${n + 1}: ${msg}`));
    rows.push(o);
  });
  if (issues.length) {
    const top = issues.slice(0, 8);
    if (issues.length > 8) top.push(`… and ${issues.length - 8} more`);
    throw new Error(top.join("\n"));
  }
  if (rows.length === 0) throw new Error("no data rows");
  return rows;
}

function dupCheck(seen: Map<string, number>, key: string, what: string, addErr: (m: string) => void) {
  if (!key) return;
  const n = (seen.get(key) ?? 0) + 1;
  seen.set(key, n);
  if (n === 2) addErr(`${what} '${key}' appears more than once in the sheet`);
}

/** Parts bulk enroll — ONE KIND PER SHEET. The kind picks the template, and
 *  the template picks the columns: connected kinds carry connection columns,
 *  dumb kinds must not even have them. */
export function parsePartsCsvFor(kind: string): (text: string) => PartRowIn[] {
  const conn = CONN_KINDS.has(kind);
  const required = conn ? ["version_id", "serial_no", "connection_id"] : ["version_id", "serial_no"];
  const optional = [...(conn ? ["board_id", "iccid", "imsi"] : ["board_id"]), "ble_mac", "manufactured_at", "warranty_until", "lot"];
  return (text) => {
    const serials = new Map<string, number>(), imeis = new Map<string, number>(), macs = new Map<string, number>();
    const rows = parseStrict(text, required, optional, (o, err) => {
      if (o.version_id && !UUID_RE.test(o.version_id)) err(`'${o.version_id}' is not a model id (uuid - Asset Catalog → Overview → drawer)`);
      if (o.serial_no && !SERIAL_CSV_RE.test(o.serial_no)) err(`bad serial '${o.serial_no}'`);
      if (conn && o.connection_id && !/^\d{15}$/.test(o.connection_id)) err(`connection_id must be a 15-digit IMEI`);
      if (o.ble_mac && !MAC_CSV_RE.test(o.ble_mac)) err(`bad BLE MAC '${o.ble_mac}' (AA:BB:CC:DD:EE:FF)`);
      for (const dcol of ["manufactured_at", "warranty_until"]) if (o[dcol] && !DATE_RE.test(o[dcol])) err(`'${o[dcol]}' is not a date (YYYY-MM-DD)`);
      dupCheck(serials, o.serial_no, "serial", err);
      dupCheck(imeis, o.connection_id ?? "", "IMEI", err);
      dupCheck(macs, o.ble_mac ?? "", "BLE MAC", err);
    });
    return rows.map((o) => ({ kind, version_id: o.version_id, serial_no: o.serial_no,
      connection_id: o.connection_id || null, ble_mac: o.ble_mac || null, board_id: o.board_id || null,
      iccid: o.iccid || null, imsi: o.imsi || null, manufactured_at: o.manufactured_at || null,
      warranty_until: o.warranty_until || null, lot: o.lot || null }));
  };
}

/** Assets bulk enroll — one kind per sheet: the vehicle template carries a
 *  mandatory number_plate column; the battery template has no label at all. */
export function parseAssetsCsvFor(kind: "vehicle" | "battery"): (text: string) => AssetRowIn[] {
  return (text) => {
    const ids = new Map<string, number>();
    const rows = parseStrict(text,
      kind === "vehicle" ? ["asset_id", "version_id", "number_plate"] : ["asset_id", "version_id"], [],
      (o, err) => {
        if (o.asset_id && !SERIAL_CSV_RE.test(o.asset_id)) err(`bad asset id '${o.asset_id}'`);
        if (o.version_id && !UUID_RE.test(o.version_id)) err(`'${o.version_id}' is not a model id (uuid)`);
        dupCheck(ids, o.asset_id, "asset id", err);
      });
    return rows.map((o) => ({ asset_id: o.asset_id, version_id: o.version_id, label: o.number_plate || null }));
  };
}

/** Bulk allocate — ONE WHAT PER SHEET: connections (imei), vehicles (vin),
 *  batteries (bin) or parts (serial_no); the column name matches the what. */
export const ALLOC_WHATS = [
  { id: "connection", label: "Connections (IMEI)", col: "imei" },
  { id: "vehicle", label: "Vehicles (VIN)", col: "vin" },
  { id: "battery", label: "Batteries (BIN)", col: "bin" },
  { id: "part", label: "Parts (serial)", col: "serial_no" },
] as const;

export function parseAllocateCsvFor(what: string): (text: string) => string[] {
  const spec = ALLOC_WHATS.find((w) => w.id === what)!;
  return (text) => {
    const ids = new Map<string, number>();
    const rows = parseStrict(text, [spec.col], [], (o, err) => {
      const v = o[spec.col];
      if (what === "connection" && v && !/^\d{15}$/.test(v)) err(`'${v}' is not a 15-digit IMEI`);
      if (what !== "connection" && v && !SERIAL_CSV_RE.test(v)) err(`bad ${spec.col} '${v}'`);
      dupCheck(ids, v, spec.col, err);
    });
    return rows.map((o) => o[spec.col]);
  };
}
